import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Avatar, Chip,
  TextField, Button, CircularProgress, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  IconButton, Tooltip, InputAdornment, Menu, MenuItem, ListItemIcon, ListItemText,
  ToggleButton, ToggleButtonGroup, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  PlaylistAddCheck as SheetIcon, Search as SearchIcon, Mic as MicIcon,
  MicNone as MicOffIcon, Cancel as CancelIcon,
  AttachMoney as MoneyIcon, Today as TodayIcon,
  CheckCircle as DoneIcon, AccessTime as PendingIcon,
  Delete as DeleteIcon, AssignmentInd as AssignIcon,
  Class as ClassIcon, ChevronRight as ChevronIcon,
  CheckCircleOutline as SavedIcon, ErrorOutline as ErrorOutIcon,
  Logout as LogoutIcon, Refresh as RefreshIcon, School as SchoolIcon,
  ArrowBack as BackIcon, SwapHoriz as SwitchIcon, Menu as MenuIconM,
  ViewSidebar as MenuViewIcon, Add as AddIcon, Edit as EditIcon,
  PriceCheck as ChangeGivenIcon, ReportProblem as ChangeOwedIcon,
  EventAvailable as DailyIcon, Group as GroupIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const FONT_BOOK = `'Source Serif Pro', 'Georgia', serif`;
const FONT_UI   = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayStr = () => new Date().toISOString().slice(0, 10);

// ── Kiosk-mode helper (mirrors DashboardLayout) ─────────────────
function setKioskModePref(value) {
  if (value === null) localStorage.removeItem('kioskMode');
  else localStorage.setItem('kioskMode', value ? 'kiosk' : 'normal');
  window.dispatchEvent(new Event('kioskModeChange'));
}
function useKioskMode() {
  const [val, setVal] = useState(() => localStorage.getItem('kioskMode'));
  useEffect(() => {
    const sync = () => setVal(localStorage.getItem('kioskMode'));
    window.addEventListener('kioskModeChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('kioskModeChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return val;
}

// ── Web Speech recognition wrapper ─────────────────────────────
function getSpeechRecognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function VoiceMicButton({ onText, disabled, size = 'medium' }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const SR = getSpeechRecognition();
  if (!SR) return null;

  const start = () => {
    try {
      const rec = new SR();
      rec.lang = 'en-US';
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.continuous = false;
      rec.onresult = (e) => {
        const t = e.results?.[0]?.[0]?.transcript || '';
        if (t) onText(t.trim());
      };
      rec.onend   = () => setListening(false);
      rec.onerror = () => setListening(false);
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch { setListening(false); }
  };
  const stop = () => { try { recRef.current?.stop(); } catch {} setListening(false); };

  return (
    <Tooltip title={listening ? 'Listening…' : 'Voice search'}>
      <span>
        <IconButton size={size} onClick={listening ? stop : start} disabled={disabled}
          sx={{
            color: listening ? 'error.main' : 'primary.main',
            ...(listening && { animation: 'pulse 1.2s infinite' }),
            '@keyframes pulse': {
              '0%':   { boxShadow: '0 0 0 0 rgba(211,47,47,0.5)' },
              '70%':  { boxShadow: '0 0 0 12px rgba(211,47,47,0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0)' },
            },
          }}>
          {listening ? <MicIcon /> : <MicOffIcon />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// Tiny WebAudio "ding" — short success cue
function playDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine';
    o.frequency.setValueAtTime(880, ctx.currentTime);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
    o.start(); o.stop(ctx.currentTime + 0.2);
    setTimeout(() => ctx.close && ctx.close(), 400);
  } catch { /* swallow */ }
}

// ── A single clickable tile for an assignment (overview mode) ──
function AssignmentTile({ group, savedToday, onOpen }) {
  return (
    <Card onClick={onOpen}
      sx={{
        cursor: 'pointer', userSelect: 'none', borderRadius: 2, overflow: 'hidden',
        transition: 'transform .15s, box-shadow .15s, border-color .15s',
        border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: 5,
          borderColor: (t) => alpha(t.palette.primary.main, 0.6),
        },
      }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', px: 2.2, py: 1.6,
        display: 'flex', alignItems: 'center', gap: 1.2,
      }}>
        <Avatar sx={{ width: 42, height: 42, bgcolor: 'rgba(255,255,255,0.22)' }}>
          <AssignIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }} noWrap>
            {group.income_type}
          </Typography>
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ opacity: 0.9 }}>
            {group.class_name
              ? <><ClassIcon sx={{ fontSize: 14 }} /><Typography variant="caption" fontWeight={700}>{group.class_name}</Typography></>
              : <Typography variant="caption" fontWeight={700}>All active students</Typography>}
          </Stack>
        </Box>
        <ChevronIcon />
      </Box>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.6 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 800, fontSize: 10 }}>
            Students
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={TNUM}>{group.students.length}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 800, fontSize: 10 }}>
            Saved today
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ ...TNUM, color: 'success.dark' }}>
            {savedToday}
          </Typography>
        </Box>
        {Number(group.expected_amount) > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 800, fontSize: 10 }}>
                Expected
              </Typography>
              <Typography variant="subtitle1" fontWeight={800} sx={TNUM}>
                GHS {fmtMoney(group.expected_amount)}
              </Typography>
            </Box>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" color="primary" sx={{ fontWeight: 700 }}>
          Open
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Booklet panel (no tile header — used in focus mode) ────────
// Auto-saves a row when the amount stops changing for ~700ms; once a row
// is saved, it locks. Pending entries can still be removed from the
// audit trail at the bottom of the overview page.
function BookletPanel({ group, onSaved, schoolName, schoolMotto, fullBleed = false }) {
  const theme = useTheme();
  const isPhone   = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const [search, setSearch] = useState('');
  // Status filter for the booklet rows. 'all' | 'todo' | 'saved' | 'owed'
  const [filter, setFilter] = useState('all');
  // 'auto' picks card layout on phone, table on desktop. Users can force.
  const [viewMode, setViewMode] = useState(() =>
    localStorage.getItem('bookletView') || 'auto');
  const setViewModePersisted = (v) => {
    if (v) { setViewMode(v); localStorage.setItem('bookletView', v); }
  };
  const useCards = viewMode === 'cards' || (viewMode === 'auto' && isCompact);
  const [rows, setRows] = useState({});
  const timersRef = useRef({});
  const rowsRef = useRef({});
  rowsRef.current = rows;

  // Pre-seed from server-side already_today data
  useEffect(() => {
    const seed = {};
    for (const s of group.students) {
      if (s.already_today) {
        seed[s.trans_code] = {
          status: s.already_today.status === 'APPROVED' ? 'approved' : 'saved',
          collection_id: s.already_today.collection_id,
          saved_received: Number(s.already_today.amount_received || 0),
          saved_taking:   Number(s.already_today.net_amount      || 0),
          saved_change:   Number(s.already_today.change_given    || 0),
          saved_handed:   s.already_today.change_handed === 0 ? 0 : 1,
          hasExisting:    true,
          addMode:        false,
          received: '', taking: '',
        };
      }
    }
    setRows(seed);
    setSearch('');
  }, [group.assignment_id, group.students]);

  // Flush any pending debounce timers on unmount
  useEffect(() => () => {
    Object.values(timersRef.current).forEach(t => t && clearTimeout(t));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = group.students;
    if (q) {
      out = out.filter(s =>
        (s.name || '').toLowerCase().includes(q) ||
        String(s.index_no || '').toLowerCase().includes(q) ||
        (s.class_name || '').toLowerCase().includes(q)
      );
    }
    if (filter !== 'all') {
      out = out.filter(s => {
        const r = rows[s.trans_code];
        const has = r?.hasExisting;
        const handed = r?.saved_handed !== 0;
        const ch = Number(r?.saved_change || 0);
        if (filter === 'todo')  return !has;
        if (filter === 'saved') return has;
        if (filter === 'owed')  return has && ch > 0 && !handed;
        return true;
      });
    }
    return out;
  }, [group.students, search, filter, rows]);

  // Counts for the filter chip badges
  const filterCounts = useMemo(() => {
    let saved = 0, owed = 0;
    for (const s of group.students) {
      const r = rows[s.trans_code];
      if (r?.hasExisting) {
        saved++;
        const ch = Number(r?.saved_change || 0);
        if (ch > 0 && r?.saved_handed === 0) owed++;
      }
    }
    return {
      all:   group.students.length,
      todo:  group.students.length - saved,
      saved,
      owed,
    };
  }, [rows, group.students]);

  const stats = useMemo(() => {
    let saved = 0, total = 0, owed = 0;
    Object.values(rows).forEach(r => {
      if (r.status === 'saved' || r.status === 'approved') {
        saved++;
        total += Number(r.saved_taking || 0);
        if (!r.saved_handed) owed += Number(r.saved_change || 0);
      }
    });
    return { saved, total, owed, count: group.students.length };
  }, [rows, group.students.length]);

  const setRow = (id, patch) =>
    setRows(prev => {
      const cur = prev[id] || {
        status: 'idle', received: '', taking: '',
        hasExisting: false, addMode: false,
      };
      return { ...prev, [id]: { ...cur, ...patch } };
    });

  const saveRow = async (s) => {
    const cur = rowsRef.current[s.trans_code];
    if (!cur) return;
    const recv = Number(cur.received) || 0;
    if (recv <= 0) return;
    // taking defaults to received when blank — common case where the
    // collector takes the full amount the student handed over.
    const take = cur.taking === '' || cur.taking === undefined
      ? recv
      : (Number(cur.taking) || 0);
    if (take <= 0) return;
    const change = Math.max(0, recv - take);
    const changeHanded = cur.changeHanded === 0 ? 0 : 1;
    if (cur.status === 'saving') return;

    // Path 1a: existing PENDING entry + correctMode → REPLACE (PUT)
    // Used when the collector realises the amount they entered earlier was
    // wrong (too high or too low) and wants to overwrite — not accumulate.
    if (cur.hasExisting && cur.correctMode && cur.status === 'saved' && cur.collection_id) {
      setRow(s.trans_code, { status: 'saving', error: '' });
      try {
        const r = await API.put(`/collections/${cur.collection_id}`, {
          expected_amount: Number(group.expected_amount) || 0,
          amount_received: recv,
          net_amount:      take,
          change_given:    change,
          change_handed:   changeHanded,
        });
        setRow(s.trans_code, {
          status: 'saved', correctMode: false,
          received: '', taking: '',
          saved_received: r.data?.amount_received ?? recv,
          saved_taking:   r.data?.net_amount      ?? take,
          saved_change:   r.data?.change_given    ?? change,
          saved_handed:   r.data?.change_handed   ?? changeHanded,
        });
        playDing();
        onSaved && onSaved();
      } catch (e) {
        setRow(s.trans_code, { status: 'error', error: e?.response?.data?.message || e.message });
      }
      return;
    }

    // Path 1b: existing PENDING entry + addMode → top up (sums)
    if (cur.hasExisting && cur.addMode && cur.status === 'saved' && cur.collection_id) {
      setRow(s.trans_code, { status: 'saving', error: '' });
      try {
        const r = await API.post(`/collections/${cur.collection_id}/topup`, {
          amount_received: recv,
          net_amount:      take,
          change_given:    change,
          change_handed:   changeHanded,
        });
        setRow(s.trans_code, {
          status: 'saved', addMode: false,
          received: '', taking: '',
          saved_received: r.data?.amount_received || (cur.saved_received + recv),
          saved_taking:   r.data?.net_amount      || (cur.saved_taking   + take),
          saved_change:   r.data?.change_given    || (cur.saved_change   + change),
          saved_handed:   r.data?.change_handed ?? changeHanded,
        });
        playDing();
        onSaved && onSaved();
      } catch (e) {
        setRow(s.trans_code, { status: 'error', error: e?.response?.data?.message || e.message });
      }
      return;
    }

    // Path 2: existing APPROVED → can't merge into the legacy ledger
    if (cur.hasExisting && cur.addMode && cur.status === 'approved') {
      const ok = await swal.confirm({
        title: 'Already approved',
        text: `${s.name}'s earlier ${group.income_type} entry has been approved by a supervisor and posted to the ledger — it can't be merged. Save this as a SECOND entry instead?`,
        confirmText: 'Yes, save as new',
        tone: 'warning',
      });
      if (!ok) {
        setRow(s.trans_code, { addMode: false, received: '', taking: '' });
        return;
      }
      // fall through to fresh insert below
    }

    // Path 3: fresh insert
    setRow(s.trans_code, { status: 'saving', error: '' });
    try {
      const r = await API.post('/collections', {
        student_id:    s.index_no,
        student_name:  s.name,
        class_name:    s.class_name,
        income_type:   group.income_type,
        expected_amount: Number(group.expected_amount) || 0,
        amount_received: recv,
        change_given:   change,
        net_amount:     take,
        change_handed:  changeHanded,
        payment_date:   todayStr(),
        notes:          '',
        allow_partial:  take < (Number(group.expected_amount) || 0),
      });
      setRow(s.trans_code, {
        status: 'saved',
        collection_id: r.data?.collection_id || null,
        saved_received: recv, saved_taking: take,
        saved_change: change, saved_handed: changeHanded,
        hasExisting: true, addMode: false,
        received: '', taking: '',
      });
      playDing();
      onSaved && onSaved();
    } catch (e) {
      setRow(s.trans_code, { status: 'error', error: e?.response?.data?.message || e.message });
    }
  };

  // Save fires only when focus leaves the *whole row* (or on Enter) — not
  // mid-typing. The blur of one field starts a short timer; if focus lands
  // on the sibling field within that window, we cancel the save.
  const onReceivedChange = (s, value) =>
    setRow(s.trans_code, { received: value, status: 'idle', error: '' });
  const onTakingChange   = (s, value) =>
    setRow(s.trans_code, { taking: value, status: 'idle', error: '' });
  const onChangeHandedToggleDraft = (s, handed) =>
    setRow(s.trans_code, { changeHanded: handed ? 1 : 0 });

  const onFieldFocus = (s) => {
    if (timersRef.current[s.trans_code]) {
      clearTimeout(timersRef.current[s.trans_code]);
      timersRef.current[s.trans_code] = null;
    }
  };
  const onFieldBlur = (s) => {
    if (timersRef.current[s.trans_code]) clearTimeout(timersRef.current[s.trans_code]);
    timersRef.current[s.trans_code] = setTimeout(() => {
      timersRef.current[s.trans_code] = null;
      saveRow(s);
    }, 250);
  };
  const flushAndSave = (s) => {
    if (timersRef.current[s.trans_code]) {
      clearTimeout(timersRef.current[s.trans_code]);
      timersRef.current[s.trans_code] = null;
    }
    saveRow(s);
  };
  const onAmountKey = (s, e) => {
    if (e.key === 'Enter') { e.preventDefault(); flushAndSave(s); }
  };

  // Click "+ Add more" on an already-saved row — prompt then unlock for top-up.
  const enterAddMode = async (s) => {
    const cur = rowsRef.current[s.trans_code];
    if (!cur) return;
    const ok = await swal.confirm({
      title: 'Already paid today',
      text: `${s.name} already paid GHS ${fmtMoney(cur.saved_received)} for ${group.income_type} today (status: ${cur.status === 'approved' ? 'APPROVED' : 'PENDING'}). Add another amount on top — totals will accumulate?`,
      confirmText: 'Yes, add more',
      tone: 'primary',
    });
    if (!ok) return;
    setRow(s.trans_code, { addMode: true, correctMode: false, received: '', taking: '', error: '' });
  };

  // Click "✎ Correct" on a PENDING row — pre-fills the inputs with the
  // existing values so the collector can revise them up or down.
  // Only available while the entry is still PENDING.
  const enterCorrectMode = (s) => {
    const cur = rowsRef.current[s.trans_code];
    if (!cur) return;
    setRow(s.trans_code, {
      correctMode: true, addMode: false,
      received: String(cur.saved_received ?? ''),
      taking:   String(cur.saved_taking   ?? ''),
      changeHanded: cur.saved_handed,
      error: '',
    });
  };

  // Toggle change-handed on a saved row.
  const toggleChangeGiven = async (s) => {
    const cur = rowsRef.current[s.trans_code];
    if (!cur?.collection_id) return;
    const next = cur.saved_handed ? 0 : 1;
    try {
      await API.post(`/collections/${cur.collection_id}/change-status`, { handed: next === 1 });
      setRow(s.trans_code, { saved_handed: next });
      onSaved && onSaved();
    } catch (e) { swal.error(swal.pickError(e, 'Update failed')); }
  };

  const StatusBadge = ({ status }) => {
    if (status === 'saving')   return <CircularProgress size={18} />;
    if (status === 'saved')    return <Chip size="small" color="warning" label="PENDING" sx={{ fontWeight: 800, height: 22 }} />;
    if (status === 'approved') return <Chip size="small" color="success" icon={<DoneIcon sx={{ fontSize: 14 }} />} label="PAID" sx={{ fontWeight: 800, height: 22 }} />;
    if (status === 'error')    return <Tooltip title="Save failed — try again"><Chip size="small" color="error" icon={<ErrorOutIcon sx={{ fontSize: 14 }} />} label="RETRY" sx={{ fontWeight: 800, height: 22 }} /></Tooltip>;
    return null;
  };

  return (
    <Box sx={{
      bgcolor: '#fafafa', p: { xs: 1.5, md: 2.2 },
      ...(fullBleed && {
        minHeight: '100vh',
        position: 'relative',
        // Reserve a row of space at the top so the floating action pill
        // (which is fixed at top:16, right:16) doesn't overlap the search
        // bar's voice-mic / clear-input adornments.
        pt: { xs: 9, md: 9 },
      }),
    }}>
      {/* Search + voice */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems="center" sx={{ mb: 1.5 }}>
        <TextField fullWidth size="medium" autoFocus
          placeholder="Search by name, class, or index number… or use the mic"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: (
              <InputAdornment position="end">
                <VoiceMicButton onText={(t) => setSearch(t)} />
              </InputAdornment>
            ),
            sx: { bgcolor: '#fff', fontSize: 16 },
          }} />
        {search && (
          <Button size="small" onClick={() => setSearch('')}
            startIcon={<CancelIcon fontSize="small" />} color="inherit">
            Clear
          </Button>
        )}
      </Stack>

      {/* Filter chips + view-mode toggle (compact on phone) */}
      <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mb: 1.2, flexWrap: 'wrap', rowGap: 0.7 }}>
        <Chip size="small" clickable color={filter === 'all'   ? 'primary' : 'default'}
          variant={filter === 'all'   ? 'filled'  : 'outlined'}
          label={`All · ${filterCounts.all}`}
          onClick={() => setFilter('all')}
          sx={{ fontWeight: 700 }} />
        <Chip size="small" clickable color={filter === 'todo'  ? 'primary' : 'default'}
          variant={filter === 'todo'  ? 'filled'  : 'outlined'}
          label={`To collect · ${filterCounts.todo}`}
          onClick={() => setFilter('todo')}
          sx={{ fontWeight: 700 }} />
        <Chip size="small" clickable color={filter === 'saved' ? 'success' : 'default'}
          variant={filter === 'saved' ? 'filled'  : 'outlined'}
          label={`Saved · ${filterCounts.saved}`}
          onClick={() => setFilter('saved')}
          sx={{ fontWeight: 700 }} />
        {filterCounts.owed > 0 && (
          <Chip size="small" clickable color={filter === 'owed' ? 'warning' : 'default'}
            variant={filter === 'owed' ? 'filled' : 'outlined'}
            icon={<ChangeOwedIcon sx={{ fontSize: 14 }} />}
            label={`Change owed · ${filterCounts.owed}`}
            onClick={() => setFilter('owed')}
            sx={{ fontWeight: 700 }} />
        )}
        <Box sx={{ flex: 1 }} />
        {!isPhone && (
          <ToggleButtonGroup exclusive size="small" value={viewMode}
            onChange={(_, v) => setViewModePersisted(v)}
            sx={{ '& .MuiToggleButton-root': { px: 1.2, py: 0.3, fontSize: 11, fontWeight: 700 } }}>
            <ToggleButton value="auto">Auto</ToggleButton>
            <ToggleButton value="table">Table</ToggleButton>
            <ToggleButton value="cards">Cards</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {/* Booklet sheet */}
      <Paper variant="outlined" sx={{
        borderRadius: 1, overflow: 'hidden',
        boxShadow: '0 1px 0 #e5e5e5, 0 8px 18px rgba(0,0,0,0.06)',
        position: 'relative',
      }}>
        <Box sx={{
          px: 2.5, py: 1.8, borderBottom: '2px solid', borderColor: 'text.primary',
          bgcolor: '#fff', textAlign: 'center', fontFamily: FONT_BOOK,
        }}>
          <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="center">
            <SchoolIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: 0.5, fontFamily: FONT_BOOK }}>
              {schoolName || 'School Collection Sheet'}
            </Typography>
          </Stack>
          {schoolMotto && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
              {schoolMotto}
            </Typography>
          )}
          <Typography variant="subtitle2" fontWeight={800} sx={{ mt: 0.6, textTransform: 'uppercase', letterSpacing: 1 }}>
            {group.income_type} {group.class_name ? `· ${group.class_name}` : '· All Students'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </Typography>
        </Box>

        {useCards && (
          <Box sx={{ p: { xs: 1, sm: 1.4 }, display: 'grid', gap: 1.2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, bgcolor: '#fff' }}>
            {filtered.length === 0 && (
              <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary',
                gridColumn: '1 / -1' }}>
                {search ? 'No students match your search.' : 'No students in this view.'}
              </Box>
            )}
            {filtered.map((s) => {
              const row = rows[s.trans_code] || {
                status: 'idle', received: '', taking: '',
                hasExisting: false, addMode: false, correctMode: false,
              };
              const isEditing = (!row.hasExisting || row.addMode || row.correctMode) && row.status !== 'saving';
              const draftRecv = Number(row.received) || 0;
              const draftTake = (row.taking === '' || row.taking === undefined)
                ? draftRecv : (Number(row.taking) || 0);
              const draftChange = Math.max(0, draftRecv - draftTake);
              const cardBg =
                row.status === 'saved' && !row.addMode && !row.correctMode ? '#f1f8e9'
                : row.status === 'approved' && !row.addMode ? '#e8f5e9'
                : row.status === 'error' ? '#ffebee'
                : '#fff';
              return (
                <Paper key={s.trans_code} variant="outlined"
                  sx={{ p: 1.4, bgcolor: cardBg, borderRadius: 2 }}>
                  {/* Header row: avatar + name + status */}
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                    <Avatar src={s.image || undefined}
                      sx={{ width: 42, height: 42, bgcolor: 'primary.light', fontSize: 16 }}>
                      {(s.name?.[0] || '?').toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={800} noWrap>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={TNUM}>
                        {s.class_name || '—'} · {s.index_no}
                      </Typography>
                    </Box>
                    {row.hasExisting && !row.addMode && !row.correctMode && (
                      row.status === 'approved'
                        ? <Chip size="small" color="success" icon={<DoneIcon sx={{ fontSize: 14 }} />} label="PAID" sx={{ fontWeight: 800 }} />
                        : <Chip size="small" color="warning" label="PENDING" sx={{ fontWeight: 800 }} />
                    )}
                    {row.status === 'saving' && <CircularProgress size={18} />}
                  </Stack>

                  {/* Saved-state strip */}
                  {row.hasExisting && !row.addMode && !row.correctMode && (
                    <Box sx={{ ...TNUM, fontFamily: FONT_BOOK, mb: 1 }}>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={0.5}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Received</Typography>
                          <Typography fontWeight={800}>GHS {fmtMoney(row.saved_received)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Taking</Typography>
                          <Typography fontWeight={800} color="success.dark">GHS {fmtMoney(row.saved_taking)}</Typography>
                        </Box>
                        {Number(row.saved_change) > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">Change</Typography>
                            <Tooltip title={row.saved_handed ? 'Click to mark as still owed' : 'Click once you hand it back'}>
                              <Chip size="small" clickable
                                onClick={() => toggleChangeGiven(s)}
                                color={row.saved_handed ? 'success' : 'warning'}
                                variant={row.saved_handed ? 'filled' : 'outlined'}
                                icon={row.saved_handed
                                  ? <ChangeGivenIcon sx={{ fontSize: 13 }} />
                                  : <ChangeOwedIcon sx={{ fontSize: 13 }} />}
                                label={`GHS ${fmtMoney(row.saved_change)} · ${row.saved_handed ? 'Given' : 'Owed'}`}
                                sx={{ fontWeight: 800, height: 22, mt: 0.4 }} />
                            </Tooltip>
                          </Box>
                        )}
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {row.status === 'saved' && (
                          <Button size="small" variant="outlined" color="warning"
                            startIcon={<EditIcon fontSize="small" />}
                            onClick={() => enterCorrectMode(s)}
                            sx={{ fontWeight: 700, flex: 1 }}>
                            Correct
                          </Button>
                        )}
                        <Button size="small" variant="contained" color="primary"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={() => enterAddMode(s)}
                          sx={{ fontWeight: 700, flex: 1 }}>
                          Add more
                        </Button>
                      </Stack>
                    </Box>
                  )}

                  {/* Editing strip */}
                  {isEditing && (
                    <Stack spacing={1}>
                      {row.addMode && (
                        <Typography variant="caption" color="primary" fontWeight={700}>
                          Adding on top — current total: GHS {fmtMoney(row.saved_received)} / GHS {fmtMoney(row.saved_taking)}.
                        </Typography>
                      )}
                      {row.correctMode && (
                        <Typography variant="caption" color="warning.dark" fontWeight={700}>
                          Correcting — values will REPLACE the existing entry (was GHS {fmtMoney(row.saved_received)} / GHS {fmtMoney(row.saved_taking)}).
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1}>
                        <TextField size="small" type="number" fullWidth
                          label="Received" value={row.received}
                          onChange={(e) => onReceivedChange(s, e.target.value)}
                          onFocus={() => onFieldFocus(s)}
                          onBlur={() => onFieldBlur(s)}
                          onKeyDown={(e) => onAmountKey(s, e)}
                          placeholder={Number(group.expected_amount) > 0 ? String(group.expected_amount) : '0.00'}
                          inputProps={{ style: { textAlign: 'right', fontSize: 16, fontWeight: 800, ...TNUM }, inputMode: 'decimal' }}
                          sx={{ bgcolor: '#fff' }}
                        />
                        <TextField size="small" type="number" fullWidth
                          label="Taking" value={row.taking}
                          onChange={(e) => onTakingChange(s, e.target.value)}
                          onFocus={() => onFieldFocus(s)}
                          onBlur={() => onFieldBlur(s)}
                          onKeyDown={(e) => onAmountKey(s, e)}
                          placeholder={String(row.received || (Number(group.expected_amount) || ''))}
                          inputProps={{ style: { textAlign: 'right', fontSize: 16, fontWeight: 800, ...TNUM }, inputMode: 'decimal' }}
                          sx={{ bgcolor: '#fff' }}
                        />
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {draftChange > 0 ? (
                          <Stack direction="row" alignItems="center" spacing={0.6} sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary">Change</Typography>
                            <Typography variant="body2" fontWeight={800} color="warning.dark" sx={TNUM}>
                              GHS {fmtMoney(draftChange)}
                            </Typography>
                            <Chip size="small" clickable
                              onClick={() => onChangeHandedToggleDraft(s, row.changeHanded === 0)}
                              color={row.changeHanded === 0 ? 'warning' : 'success'}
                              variant={row.changeHanded === 0 ? 'outlined' : 'filled'}
                              icon={row.changeHanded === 0
                                ? <ChangeOwedIcon sx={{ fontSize: 13 }} />
                                : <ChangeGivenIcon sx={{ fontSize: 13 }} />}
                              label={row.changeHanded === 0 ? 'Owed' : 'Given'}
                              sx={{ fontWeight: 700, height: 22 }} />
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>No change</Typography>
                        )}
                        {(row.addMode || row.correctMode) && (
                          <Button size="small" color="inherit"
                            onClick={() => setRow(s.trans_code, { addMode: false, correctMode: false, received: '', taking: '' })}
                            startIcon={<CancelIcon fontSize="small" />}>
                            Cancel
                          </Button>
                        )}
                        <Button size="small" variant="contained" color="success"
                          onClick={() => flushAndSave(s)}
                          disabled={(Number(row.received) || 0) <= 0 || row.status === 'saving'}
                          startIcon={<SavedIcon fontSize="small" />}
                          sx={{ fontWeight: 700 }}>
                          Save
                        </Button>
                      </Stack>
                      {row.error && (
                        <Typography variant="caption" color="error">{row.error}</Typography>
                      )}
                    </Stack>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}

        {!useCards && (
        <TableContainer>
          <Table size="small" sx={{
            bgcolor: '#fff',
            '& .MuiTableCell-head': {
              bgcolor: 'grey.100',
              fontWeight: 800, fontSize: 11, textTransform: 'uppercase',
              letterSpacing: 0.5, color: 'text.primary',
              borderBottom: '2px solid', borderColor: 'text.primary',
            },
            '& .MuiTableCell-root': { borderRight: '1px solid', borderColor: 'divider' },
            '& .MuiTableCell-root:last-of-type': { borderRight: 0 },
            '& tbody .MuiTableRow-root:nth-of-type(odd)': { bgcolor: '#fbfbfb' },
          }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: 44 }}>S/N</TableCell>
                <TableCell sx={{ width: 56 }}></TableCell>
                <TableCell>Student</TableCell>
                <TableCell sx={{ width: 90 }}>Class</TableCell>
                <TableCell sx={{ width: 380 }}>Received · Taking · Change</TableCell>
                <TableCell align="center" sx={{ width: 56 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    {search ? 'No students match your search.' : 'No active students in this scope.'}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((s, i) => {
                const row = rows[s.trans_code] || {
                  status: 'idle', received: '', taking: '',
                  hasExisting: false, addMode: false, correctMode: false,
                };
                // Editing iff: no existing entry yet, or user clicked "+ Add more",
                // or user clicked "✎ Correct" to revise the existing amount.
                const isEditing = (!row.hasExisting || row.addMode || row.correctMode) && row.status !== 'saving';
                // Live preview of change while typing
                const draftRecv = Number(row.received) || 0;
                const draftTake = (row.taking === '' || row.taking === undefined)
                  ? draftRecv
                  : (Number(row.taking) || 0);
                const draftChange = Math.max(0, draftRecv - draftTake);
                return (
                  <TableRow key={s.trans_code} sx={{
                    '& td': { py: 1.1, fontSize: 13, fontFamily: FONT_BOOK },
                    ...(row.status === 'saved' && !row.addMode && { bgcolor: '#f1f8e9 !important' }),
                    ...(row.status === 'approved' && !row.addMode && { bgcolor: '#e8f5e9 !important' }),
                    ...(row.status === 'error' && { bgcolor: '#ffebee !important' }),
                  }}>
                    <TableCell align="center" sx={{ ...TNUM, color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell>
                      <Avatar src={s.image || undefined}
                        sx={{ width: 36, height: 36, bgcolor: 'primary.light', fontSize: 14 }}>
                        {(s.name?.[0] || '?').toUpperCase()}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={14} fontWeight={700} sx={{ fontFamily: FONT_BOOK }}>
                        {s.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={TNUM}>
                        {s.index_no}{s.gender ? ` · ${s.gender}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{s.class_name || '—'}</TableCell>
                    <TableCell>
                      {/* Saved-state strip — prominently shows what's already on file
                          for this student today: a PENDING/PAID badge, the actual
                          Received/Taking amounts, and the change status. */}
                      {row.hasExisting && !row.addMode && (
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          {row.status === 'approved'
                            ? <Chip size="small" color="success" icon={<DoneIcon sx={{ fontSize: 14 }} />} label="PAID"
                                sx={{ fontWeight: 800, height: 24 }} />
                            : <Chip size="small" color="warning" label="PENDING"
                                sx={{ fontWeight: 800, height: 24 }} />}
                          <Box sx={{ ...TNUM, fontFamily: FONT_BOOK }}>
                            <Typography component="span" variant="caption" color="text.secondary">Received </Typography>
                            <Typography component="span" fontWeight={800}>GHS {fmtMoney(row.saved_received)}</Typography>
                            <Typography component="span" variant="caption" color="text.secondary"> · Taking </Typography>
                            <Typography component="span" fontWeight={800} sx={{ color: 'success.dark' }}>GHS {fmtMoney(row.saved_taking)}</Typography>
                          </Box>
                          {Number(row.saved_change) > 0 ? (
                            <Tooltip title={row.saved_handed ? 'Change has been given to the student. Click to flip to "Owed".' : 'Change owed — click once you hand it back.'}>
                              <Chip size="small" clickable
                                onClick={() => toggleChangeGiven(s)}
                                color={row.saved_handed ? 'success' : 'warning'}
                                variant={row.saved_handed ? 'filled' : 'outlined'}
                                icon={row.saved_handed
                                  ? <ChangeGivenIcon sx={{ fontSize: 14 }} />
                                  : <ChangeOwedIcon sx={{ fontSize: 14 }} />}
                                label={`Change GHS ${fmtMoney(row.saved_change)} · ${row.saved_handed ? 'Given' : 'Owed'}`}
                                sx={{ fontWeight: 800, height: 24 }} />
                            </Tooltip>
                          ) : (
                            <Chip size="small" variant="outlined"
                              label="No change" sx={{ fontWeight: 600, height: 22, color: 'text.disabled' }} />
                          )}
                          <Box sx={{ flex: 1 }} />
                          {row.status === 'saved' && (
                            <Tooltip title="Correct the amount (replace — use when you entered it wrong)">
                              <span>
                                <IconButton size="small" color="warning"
                                  onClick={() => enterCorrectMode(s)}
                                  sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.warning.main, 0.16) } }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Add more on top of this entry (sum)">
                            <span>
                              <IconButton size="small" color="primary"
                                onClick={() => enterAddMode(s)}
                                sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) } }}>
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      )}

                      {/* Editing strip (fresh entry, top-up, or correction) */}
                      {isEditing && (
                        <Stack spacing={0.5}>
                          {row.addMode && (
                            <Typography variant="caption" color="primary" fontWeight={700}>
                              Adding on top — current total: GHS {fmtMoney(row.saved_received)} received / GHS {fmtMoney(row.saved_taking)} taken. New entry will accumulate.
                            </Typography>
                          )}
                          {row.correctMode && (
                            <Typography variant="caption" color="warning.dark" fontWeight={700}>
                              Correcting — values will REPLACE the existing entry (was GHS {fmtMoney(row.saved_received)} received / GHS {fmtMoney(row.saved_taking)} taken).
                            </Typography>
                          )}
                          <Stack direction="row" spacing={0.7} alignItems="center">
                            <TextField size="small" type="number"
                              label="Received" value={row.received}
                              onChange={(e) => onReceivedChange(s, e.target.value)}
                              onFocus={() => onFieldFocus(s)}
                              onBlur={() => onFieldBlur(s)}
                              onKeyDown={(e) => onAmountKey(s, e)}
                              placeholder={Number(group.expected_amount) > 0 ? String(group.expected_amount) : '0.00'}
                              inputProps={{ style: { textAlign: 'right', fontSize: 14, fontWeight: 700, padding: '6px 8px', ...TNUM }, inputMode: 'decimal' }}
                              sx={{ width: 110, bgcolor: '#fff' }}
                            />
                            <TextField size="small" type="number"
                              label="Taking" value={row.taking}
                              onChange={(e) => onTakingChange(s, e.target.value)}
                              onFocus={() => onFieldFocus(s)}
                              onBlur={() => onFieldBlur(s)}
                              onKeyDown={(e) => onAmountKey(s, e)}
                              placeholder={String(row.received || (Number(group.expected_amount) || ''))}
                              inputProps={{ style: { textAlign: 'right', fontSize: 14, fontWeight: 700, padding: '6px 8px', ...TNUM }, inputMode: 'decimal' }}
                              sx={{ width: 110, bgcolor: '#fff' }}
                            />
                            <Box sx={{ flex: 1, ...TNUM }}>
                              {draftChange > 0 ? (
                                <Stack direction="row" alignItems="center" spacing={0.6}>
                                  <Typography variant="caption" color="text.secondary">Change</Typography>
                                  <Typography variant="body2" fontWeight={800} color="warning.dark">
                                    GHS {fmtMoney(draftChange)}
                                  </Typography>
                                  <Tooltip title={(row.changeHanded === 0 ? 'Mark as given' : 'Mark as still owed (give later)')}>
                                    <Chip size="small" clickable
                                      onClick={() => onChangeHandedToggleDraft(s, row.changeHanded === 0)}
                                      color={row.changeHanded === 0 ? 'warning' : 'success'}
                                      variant={row.changeHanded === 0 ? 'outlined' : 'filled'}
                                      icon={row.changeHanded === 0
                                        ? <ChangeOwedIcon sx={{ fontSize: 14 }} />
                                        : <ChangeGivenIcon sx={{ fontSize: 14 }} />}
                                      label={row.changeHanded === 0 ? 'Owed' : 'Given'}
                                      sx={{ fontWeight: 700, height: 22 }} />
                                  </Tooltip>
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.disabled">No change</Typography>
                              )}
                            </Box>
                            {(row.addMode || row.correctMode) && (
                              <Tooltip title="Cancel">
                                <IconButton size="small" onClick={() => setRow(s.trans_code, { addMode: false, correctMode: false, received: '', taking: '' })}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                          {row.error && (
                            <Typography variant="caption" color="error">{row.error}</Typography>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <StatusBadge status={row.status} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Sheet footer */}
        <Box sx={{
          px: 2.5, py: 1.4, borderTop: '2px solid', borderColor: 'text.primary',
          bgcolor: '#fff', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <Typography variant="caption" sx={{ fontFamily: FONT_BOOK, fontStyle: 'italic' }}>
            Auto-saves as you type · "Taking" defaults to "Received" if blank · entries are pending until approval.
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={2} alignItems="center" sx={TNUM}>
            <Typography variant="caption" color="text.secondary">Saved</Typography>
            <Typography variant="subtitle2" fontWeight={800}>{stats.saved}</Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="subtitle1" fontWeight={800} color="success.dark">
              GHS {fmtMoney(stats.total)}
            </Typography>
            {stats.owed > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Tooltip title="Total change you still owe to students from today's collections">
                  <Stack direction="row" alignItems="center" spacing={0.6}>
                    <ChangeOwedIcon sx={{ fontSize: 16, color: 'warning.dark' }} />
                    <Typography variant="caption" color="warning.dark" fontWeight={700}>Change owed</Typography>
                    <Typography variant="subtitle1" fontWeight={800} color="warning.dark">
                      GHS {fmtMoney(stats.owed)}
                    </Typography>
                  </Stack>
                </Tooltip>
              </>
            )}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

// ── Floating action menu shown only in focus mode ─────────────
function FocusFloatingMenu({ groups, currentId, onSwitch, onBack, onShowMenu, onLogout, kioskAvailable }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const others = groups.filter(g => g.assignment_id !== currentId);

  return (
    <Box sx={{
      position: 'fixed', top: { xs: 8, md: 16 }, right: { xs: 8, md: 16 },
      zIndex: (t) => t.zIndex.modal,
      display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 1 },
      bgcolor: '#fff', borderRadius: 999, boxShadow: 6,
      px: { xs: 0.6, md: 1 }, py: { xs: 0.4, md: 0.6 },
      border: '1px solid', borderColor: 'divider',
    }}>
      <Tooltip title="Back to all collections">
        <IconButton size="small" onClick={onBack} color="primary"
          sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.1), '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.2) } }}>
          <BackIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={others.length > 0 ? 'Switch income type' : 'Only one assignment'}>
        <span>
          <IconButton size="small" disabled={others.length === 0}
            onClick={(e) => setAnchorEl(e.currentTarget)} color="secondary"
            sx={{ bgcolor: (t) => alpha(t.palette.secondary.main, 0.1), '&:hover': { bgcolor: (t) => alpha(t.palette.secondary.main, 0.2) } }}>
            <SwitchIcon />
          </IconButton>
        </span>
      </Tooltip>
      {kioskAvailable && (
        <Tooltip title="Show normal menu">
          <IconButton size="small" onClick={onShowMenu}
            sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12), color: 'warning.dark', '&:hover': { bgcolor: (t) => alpha(t.palette.warning.main, 0.22) } }}>
            <MenuViewIcon />
          </IconButton>
        </Tooltip>
      )}
      {onLogout && (
        <Tooltip title="Sign out">
          <IconButton size="small" onClick={onLogout}
            sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.1), color: 'error.main', '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.2) } }}>
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      )}
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 260, mt: 0.5 } }}>
        {others.map(g => (
          <MenuItem key={g.assignment_id}
            onClick={() => { onSwitch(g.assignment_id); setAnchorEl(null); }}>
            <ListItemIcon><AssignIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText
              primary={g.income_type}
              secondary={g.class_name || 'All active students'}
              primaryTypographyProps={{ fontWeight: 700 }}
              secondaryTypographyProps={{ variant: 'caption' }} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export default function CollectorSheetPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const kioskPref = useKioskMode();

  const [groups, setGroups]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [focusId, setFocusId] = useState(null); // assignment_id when in focus mode
  const [today, setToday]     = useState({ rows: [], totals: {} });
  const [school, setSchool]   = useState({ name: '', motto: '' });
  // Daily-collection income types the collector is also assigned to
  // (only those with at least one active subscription show up here).
  const [dailyTypes, setDailyTypes] = useState([]);
  // Whether today can be worked at all — an off day or a lapsed term.
  // Same verdict the day sheet locks on; here it only relabels the
  // cards, since the sheet they open enforces it properly.
  const [dailyDay, setDailyDay]     = useState(null);

  const isCollectorOnly = useMemo(() => {
    if (!user || user.role === 'super_admin') return false;
    const pages = user.pages || [];
    return pages.length === 1 && pages[0] === 'collector_sheet';
  }, [user]);
  // Kiosk is the default for anyone landing on My Collections — phones
  // and tablets need the full screen. Mirrors the path-aware logic in
  // DashboardLayout. The user can still flip to the regular menu via
  // the in-header toggle and the preference is sticky.
  const canCollect = !!user && (user.role === 'super_admin' || (user.pages || []).includes('collector_sheet'));
  const isKioskNow = canCollect && kioskPref !== 'normal';

  const loadAssigned = async () => {
    setLoading(true);
    try {
      const r = await API.get('/collections/assigned-students');
      setGroups(r.data?.groups || []);
    } catch { setGroups([]); }
    finally { setLoading(false); }
  };

  const loadToday = async () => {
    try {
      const r = await API.get('/collections/me/today');
      setToday(r.data || { rows: [], totals: {} });
    } catch { /* silent */ }
  };

  // Pull income types where the collector also has registered students
  // (daily_subscriptions). Drives the embedded Daily Collection panel
  // so daily kiosks live inside the same My Collections screen.
  const loadDailyTypes = async () => {
    try {
      const r = await API.get('/collections/me/daily-income-types');
      setDailyTypes(r.data?.rows || []);
      setDailyDay(r.data || null);
    } catch { setDailyTypes([]); setDailyDay(null); }
  };

  useEffect(() => {
    loadAssigned();
    loadToday();
    loadDailyTypes();
    API.get('/setup/info')
      .then(r => setSchool(r.data?.info || r.data || {}))
      .catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const showNormalMenu = () => { setKioskModePref(false); navigate('/dashboard'); };
  const useKioskView   = () => { setKioskModePref(true); };

  const deletePending = async (id) => {
    const ok = await swal.confirmDelete('this pending entry');
    if (!ok) return;
    try {
      await API.delete(`/collections/${id}`);
      swal.success('Removed');
      loadAssigned();
      loadToday();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  const markAuditChange = async (r) => {
    const next = r.change_handed ? 0 : 1;
    try {
      await API.post(`/collections/${r.collection_id}/change-status`, { handed: next === 1 });
      loadToday();
      loadAssigned();
    } catch (e) { swal.error(swal.pickError(e, 'Update failed')); }
  };

  const collectorTitle = useMemo(() =>
    user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || 'Collector',
    [user]);

  // Saved-today counts per assignment for the tile
  const savedTodayMap = useMemo(() => {
    const m = {};
    for (const r of (today.rows || [])) {
      if (r.status === 'PENDING' || r.status === 'APPROVED') {
        m[r.income_type] = (m[r.income_type] || 0) + 1;
      }
    }
    return m;
  }, [today.rows]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress />
          <Typography color="text.secondary">Loading your assignments…</Typography>
        </Stack>
      </Box>
    );
  }

  // ── Focus mode: fullscreen booklet with floating menu ──────────
  if (focusId) {
    const focused = groups.find(g => g.assignment_id === focusId);
    if (!focused) {
      // Assignment list refreshed and the one we were on is gone
      setFocusId(null);
      return null;
    }
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', m: { xs: -1.5, md: -2.5 } }}>
        <FocusFloatingMenu
          groups={groups}
          currentId={focusId}
          onBack={() => setFocusId(null)}
          onSwitch={(id) => setFocusId(id)}
          onShowMenu={isKioskNow ? showNormalMenu : null}
          onLogout={isCollectorOnly ? handleLogout : null}
          kioskAvailable={isKioskNow}
        />
        <BookletPanel
          group={focused}
          onSaved={() => loadToday()}
          schoolName={school?.name}
          schoolMotto={school?.motto}
          fullBleed
        />
      </Box>
    );
  }

  // ── Overview mode ─────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', fontFamily: FONT_UI, pb: 4 }}>
      {/* Page header */}
      <Card sx={{ mb: 2.5, overflow: 'hidden', borderRadius: 2 }}>
        <Box sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', px: { xs: 2.5, md: 3 }, py: 2.4,
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 52, height: 52 }}>
            <SheetIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
              My Collections
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {collectorTitle} · {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mr: 0.5 }}>
            <Box sx={{ textAlign: 'right', mr: 1, display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 800 }}>
                Today's Total
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={TNUM}>
                GHS {fmtMoney(today.totals?.net)}
              </Typography>
            </Box>
            <Tooltip title="Refresh">
              <IconButton onClick={() => { loadAssigned(); loadToday(); loadDailyTypes(); }} sx={{ color: '#fff' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {isKioskNow ? (
              <Tooltip title="Show normal menu">
                <IconButton onClick={showNormalMenu}
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.16)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}>
                  <MenuViewIcon />
                </IconButton>
              </Tooltip>
            ) : (
              (user?.role === 'super_admin' || (user?.pages || []).includes('collector_sheet')) && (
                <Tooltip title="Switch to kiosk view (hide menu)">
                  <IconButton onClick={useKioskView}
                    sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.16)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}>
                    <SheetIcon />
                  </IconButton>
                </Tooltip>
              )
            )}
            {isCollectorOnly && (
              <Tooltip title="Sign out">
                <IconButton onClick={handleLogout} sx={{ color: '#fff' }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      </Card>

      {/* Quick stats strip — 2 columns on phone, 4 across on tablet+ */}
      <Box sx={{
        display: 'grid', gap: 1.2, mb: 2,
        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
      }}>
        <StatPill icon={<MoneyIcon />} label="Total today" value={`GHS ${fmtMoney(today.totals?.net)}`} tone="success" />
        <StatPill icon={<DoneIcon />}  label="Approved"   value={`GHS ${fmtMoney(today.totals?.approved)}`} tone="primary" />
        <StatPill icon={<PendingIcon />} label="Pending"  value={`GHS ${fmtMoney(today.totals?.pending)}`} tone="warning" />
        <StatPill icon={<TodayIcon />} label="Entries"    value={today.rows?.length || 0} tone="secondary" />
      </Box>

      {/* Daily Collection panel — income types where the user is assigned
          AND there are registered students under them. Each card opens
          the kiosk-style attendance flow on /collections/daily. */}
      {dailyTypes.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.2, px: 0.5 }}>
            <DailyIcon fontSize="small" color="secondary" />
            <Typography variant="overline" fontWeight={800} color="text.secondary" sx={{ letterSpacing: 1 }}>
              Daily Collection
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Chip size="small" variant="outlined" color="secondary"
              label={`${dailyTypes.length} type${dailyTypes.length === 1 ? '' : 's'}`}
              sx={{ fontWeight: 700 }} />
          </Stack>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}>
            {dailyTypes.map(d => {
              const status = d.session_status || null;
              const isOpen     = status === 'open' && Number(d.is_school_day) === 1;
              const isClosed   = status === 'closed';
              const isNoSchool = status === 'no_school' || (status === 'closed' && Number(d.is_school_day) === 0);
              // A day the school does not collect on outranks every
              // session state below, because no session can exist for
              // one. "Not started" on a Saturday reads as an invitation
              // to start it.
              const isOffDay = dailyDay?.period_block_kind === 'non_school_day';
              const tone =
                isOffDay    ? 'warning' :
                isOpen      ? 'success' :
                isClosed    ? 'default' :
                isNoSchool  ? 'warning' :
                              'secondary';
              const label =
                isOffDay    ? `No collections on a ${dailyDay.day_name || 'day off'}` :
                isOpen      ? 'Open today' :
                isClosed    ? 'Closed for today' :
                isNoSchool  ? 'No school today' :
                              'Not started';
              return (
                <Card key={d.income_type}
                  onClick={() => navigate(`/collections/daily?income_type=${encodeURIComponent(d.income_type)}`)}
                  sx={{
                    borderRadius: 2, cursor: 'pointer',
                    borderLeft: (t) => `4px solid ${
                      tone === 'default' ? t.palette.text.disabled : t.palette[tone].main}`,
                    transition: 'transform 120ms, box-shadow 120ms',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                  }}>
                  <CardContent sx={{ py: 1.6 }}>
                    <Stack direction="row" alignItems="center" spacing={1.4}>
                      <Box sx={{
                        width: 42, height: 42, borderRadius: 1.5,
                        bgcolor: (t) => alpha(
                          tone === 'default' ? t.palette.text.disabled : t.palette[tone].main, 0.16),
                        color: tone === 'default' ? 'text.secondary' : `${tone}.main`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <DailyIcon />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={800} noWrap sx={{ lineHeight: 1.2 }}>
                          {d.income_type}
                        </Typography>
                        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 0.2 }}>
                          <GroupIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {d.sub_count} registered
                          </Typography>
                        </Stack>
                      </Box>
                      <ChevronIcon sx={{ color: 'text.secondary' }} />
                    </Stack>
                    <Stack direction="row" spacing={0.7} sx={{ mt: 1.2 }}>
                      <Chip size="small"
                        color={tone === 'default' ? undefined : tone}
                        variant={tone === 'default' ? 'outlined' : 'filled'}
                        label={label}
                        sx={{ fontWeight: 700, height: 22 }} />
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      {/* No assignments state */}
      {groups.length === 0 && dailyTypes.length === 0 && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <AssignIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" fontWeight={800} color="text.secondary">
              No assignments yet
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 480, mx: 'auto', mt: 0.5 }}>
              Ask an administrator to assign you an income type (e.g. Feeding, PTA Dues) before you can collect.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Assignment tile grid */}
      {groups.length > 0 && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.2, px: 0.5 }}>
            <Typography variant="overline" fontWeight={800} color="text.secondary" sx={{ letterSpacing: 1 }}>
              Pick what you're collecting
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Chip size="small" variant="outlined"
              label={`${groups.length} active assignment${groups.length === 1 ? '' : 's'}`}
              sx={{ fontWeight: 700 }} />
          </Stack>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}>
            {groups.map(g => (
              <AssignmentTile key={g.assignment_id}
                group={g}
                savedToday={savedTodayMap[g.income_type] || 0}
                onOpen={() => setFocusId(g.assignment_id)}
              />
            ))}
          </Box>
        </>
      )}

      {/* Audit: today's full sheet */}
      {today.rows?.length > 0 && (
        <Card sx={{ mt: 2.5, borderRadius: 2 }}>
          <Box sx={{ px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
            <SheetIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={800}>Today's Audit Trail</Typography>
            <Box sx={{ flex: 1 }} />
            <Chip size="small" label={`${today.rows.length} entr${today.rows.length === 1 ? 'y' : 'ies'}`}
              variant="outlined" sx={{ fontWeight: 700 }} />
          </Box>
          <TableContainer>
            <Table size="small" sx={{
              '& .MuiTableCell-head': { bgcolor: 'grey.50', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Received</TableCell>
                  <TableCell align="right">Taking</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {today.rows.map(r => (
                  <TableRow key={r.collection_id} sx={{ '& td': { fontSize: 12, ...TNUM } }}>
                    <TableCell>{(r.payment_time || '').slice(0, 5)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{r.student_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.student_id}</Typography>
                    </TableCell>
                    <TableCell>{r.class_name || '—'}</TableCell>
                    <TableCell>{r.income_type}</TableCell>
                    <TableCell align="right">GHS {fmtMoney(r.amount_received)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: 'success.dark' }}>
                      GHS {fmtMoney(r.net_amount)}
                    </TableCell>
                    <TableCell>
                      {Number(r.change_given) > 0 ? (
                        <Tooltip title={r.change_handed
                          ? 'Change has been given. Click to flip to "Owed".'
                          : 'Change still owed to student. Click to mark as Given when you hand it back.'}>
                          <Chip size="small" clickable
                            onClick={() => markAuditChange(r)}
                            color={r.change_handed ? 'success' : 'warning'}
                            variant={r.change_handed ? 'filled' : 'outlined'}
                            icon={r.change_handed
                              ? <ChangeGivenIcon sx={{ fontSize: 13 }} />
                              : <ChangeOwedIcon sx={{ fontSize: 13 }} />}
                            label={`GHS ${fmtMoney(r.change_given)} · ${r.change_handed ? 'Given' : 'Owed'}`}
                            sx={{ fontWeight: 700, height: 22 }} />
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small"
                        color={r.status === 'APPROVED' ? 'success'
                              : r.status === 'PENDING' ? 'warning'
                              : 'default'}
                        label={r.status}
                        sx={{ fontWeight: 700, height: 22 }} />
                    </TableCell>
                    <TableCell align="right">
                      {r.status === 'PENDING' && (
                        <Tooltip title="Remove pending entry">
                          <IconButton size="small" color="error" onClick={() => deletePending(r.collection_id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}

function StatPill({ icon, label, value, tone = 'primary' }) {
  return (
    <Card sx={{
      flex: 1, minWidth: 180, borderRadius: 2,
      borderLeft: (t) => `4px solid ${t.palette[tone].main}`,
    }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '50%',
          bgcolor: (t) => alpha(t.palette[tone].main, 0.14),
          color: `${tone}.main`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, ...TNUM, fontSize: 17, lineHeight: 1.2 }}>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
